import React from "react";
import { ArrowTopRightOnSquareIcon, LinkIcon } from "@heroicons/react/24/outline";
import { Button } from "@truevault/aquilo";
import { frontendPathToUrl, RouteHelpers } from "../../root/routes";

type DeveloperSetupShareCardProps = {
  devInstructionsId?: string;
};

const DEVELOPER_INSTRUCTIONS_PATH =
  /^\/instructions\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/email-domain-setup$/i;

function getSafeInstructionsUrl(instructionsUrl?: string): string | undefined {
  if (!instructionsUrl) return undefined;

  try {
    const parsedUrl = new URL(instructionsUrl, window.location.origin);
    if (
      parsedUrl.origin !== window.location.origin ||
      parsedUrl.username ||
      parsedUrl.password ||
      parsedUrl.search ||
      parsedUrl.hash ||
      !DEVELOPER_INSTRUCTIONS_PATH.test(parsedUrl.pathname)
    ) {
      return undefined;
    }
    return parsedUrl.toString();
  } catch {
    return undefined;
  }
}

export function DeveloperSetupShareCard({ devInstructionsId }: DeveloperSetupShareCardProps) {
  const [copied, setCopied] = React.useState(false);
  const [copyFailed, setCopyFailed] = React.useState(false);
  const safeInstructionsUrl = React.useMemo(() => {
    const instructionsUrl = devInstructionsId
      ? frontendPathToUrl(RouteHelpers.instructions.emailDomainSetup(devInstructionsId))
      : undefined;
    return getSafeInstructionsUrl(instructionsUrl);
  }, [devInstructionsId]);
  const disabled = !safeInstructionsUrl;

  const copyLink = async () => {
    if (!safeInstructionsUrl) return;

    try {
      await navigator.clipboard.writeText(safeInstructionsUrl);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  };

  const openInstructions = () => {
    if (!safeInstructionsUrl) return;

    const link = document.createElement("a");
    link.href = safeInstructionsUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.click();
  };

  return (
    <div className="tw-boundary mt-[24px] w-full rounded-[24px] border border-solid border-neutral-300 bg-white p-[36px] shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]">
      <div className="flex flex-col items-start">
        <h2 className="m-0 text-[18px] font-medium leading-[28px] text-neutral-900">
          Have a developer helping with setup?
        </h2>
        <p className="mb-0 mt-[6px] max-w-[590px] text-[14px] leading-[21px] text-neutral-700">
          Share all privacy email and custom-domain instructions with them. No TrueVault account is
          required.
        </p>
        <div className="mt-[12px] flex flex-wrap gap-[10px]">
          <Button color="primary" size="s" disabled={disabled} onClick={() => void copyLink()}>
            <span className="inline-flex items-center gap-[8px]">
              <LinkIcon className="h-4 w-4" />
              {copied ? "Link copied" : "Copy developer link"}
            </span>
          </Button>
          <Button color="tertiary" size="s" disabled={disabled} onClick={openInstructions}>
            <span className="inline-flex items-center gap-[8px]">
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              Open instructions
            </span>
          </Button>
        </div>
      </div>
      {copyFailed && (
        <div className="mt-[12px] text-[13px] leading-[20px] text-red-700" role="alert">
          Couldn&apos;t copy the link. Open the instructions and copy the URL from your browser.
        </div>
      )}
    </div>
  );
}
